/**
 * LocationService
 * Service for Location entity operations
 * Route: api/locations
 * Controller: LocationsController.cs
 *
 * DELETE archives (soft delete) — locations are referenced by ~20 services. Use `archive` / `restore`;
 * `delete` is the same call as `archive` (kept for CrudService compatibility).
 * LocationID 0 ("Online Campus") is a real location.
 */
import { CrudService } from '../base/CrudService';
/**
 * LocationService
 * Handles location CRUD operations and custom endpoints
 * Maps to /api/locations endpoint
 */
export class LocationService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? LocationService.DEFAULT_SERVICE_PATH,
            basePath: LocationService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== CUSTOM METHODS ====================
    /**
     * Get all active locations (IsActive = true, not archived)
     * GET /api/locations/active
     * @returns Array of active locations
     */
    async getAllActive() {
        const response = await this.getAllActiveRaw();
        return response.data ?? [];
    }
    /**
     * Get all active locations (raw)
     * @returns ResponseData with array of active locations
     */
    getAllActiveRaw() {
        const endpoint = this.getEndpoint('active', 'active');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Archived (soft-deleted) locations — Location.View
     * GET /api/locations/archived
     */
    async getArchived() {
        const response = await this.getArchivedRaw();
        return response.data ?? [];
    }
    getArchivedRaw() {
        const endpoint = this.getEndpoint('archived', 'archived');
        return this.http.get(this.buildUrl(endpoint));
    }
    /**
     * Archive a location (soft delete: isDeleted = true, isActive = false) — Location.Delete
     * DELETE /api/locations/{id}
     * @returns The archived location
     */
    async archive(id) {
        const response = await this.archiveRaw(id);
        if (!response.data) {
            throw new Error(`Failed to archive location ${id}`);
        }
        return response.data;
    }
    archiveRaw(id) {
        const endpoint = this.getEndpoint('archive', `${id}`);
        return this.http.delete(this.buildUrl(endpoint));
    }
    /**
     * Restore an archived location (it comes back inactive) — Location.Delete
     * POST /api/locations/{id}/restore
     */
    async restore(id) {
        const response = await this.restoreRaw(id);
        if (!response.data) {
            throw new Error(`Failed to restore location ${id}`);
        }
        return response.data;
    }
    restoreRaw(id) {
        const endpoint = this.getEndpoint('restore', `${id}/restore`);
        return this.http.post(this.buildUrl(endpoint), {});
    }
    /**
     * What depends on a location (children, Carpool campus flag, member count) — Location.View
     * GET /api/locations/{id}/usage
     */
    async getUsage(id) {
        const response = await this.getUsageRaw(id);
        if (!response.data) {
            throw new Error(`Usage for location ${id} not found`);
        }
        return response.data;
    }
    getUsageRaw(id) {
        const endpoint = this.getEndpoint('usage', `${id}/usage`);
        return this.http.get(this.buildUrl(endpoint));
    }
    /**
     * Get location with enriched model view by ID (archived locations included)
     * GET /api/locations/view/{id}
     * @param id - Location ID
     * @returns Enriched location model view
     */
    async getViewById(id) {
        const response = await this.getViewByIdRaw(id);
        if (!response.data) {
            throw new Error(`Location view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get location view by ID (raw)
     * @param id - Location ID
     * @returns ResponseData with enriched model view
     */
    getViewByIdRaw(id) {
        const endpoint = this.getEndpoint('view', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all location model views (non-archived, enriched with related data)
     * GET /api/locations/view
     * @returns Array of enriched location model views
     */
    async getAllViews() {
        const response = await this.getAllViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all location model views (raw)
     * @returns ResponseData with array of enriched model views
     */
    getAllViewsRaw() {
        const endpoint = this.getEndpoint('allViews', 'view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all active location model views (IsActive = true, enriched with related data)
     * GET /api/locations/active/view
     * @returns Array of active enriched location model views
     */
    async getAllActiveViews() {
        const response = await this.getAllActiveViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all active location model views (raw)
     * @returns ResponseData with array of active enriched model views
     */
    getAllActiveViewsRaw() {
        const endpoint = this.getEndpoint('activeViews', 'active/view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
}
LocationService.DEFAULT_SERVICE_PATH = 'locations';
LocationService.BASE_PATH = 'api/locations';
//# sourceMappingURL=LocationService.js.map